/**
 * Browser URL Utilities
 * Builds external URLs for browser farm endpoints (screencast, agent stream, etc.)
 * from pod names extracted from internal Kubernetes URLs.
 */

import { getApiBaseUrl } from '@/config/browserFarm';

/**
 * V2 COMPAT — Extracts the pod name from a V2 browser farm URL.
 * V4 URLs use /sessions/{id}/ paths instead. Remove when V2 is retired.
 *
 * The internal K8s URL uses /display/{pod-name} or /vnc/{pod-name} as its
 * path format, depending on which service generated the URL:
 * - browser-session service: /display/{pod-name}
 * - lifecycle-manager service: /vnc/{pod-name}
 *
 * @param url - The browser farm URL (internal or external)
 * @returns The pod name or null if not found
 */
export function extractPodNameFromUrl(url: string): string | null {
  const match = url.match(/\/(?:display|vnc)\/([^/?]+)/);
  return match ? match[1] : null;
}

/**
 * V2 COMPAT — Builds the agent stream SSE URL for a V2 pod name.
 * V4 uses resolveAgentStreamUrl() directly. Remove when V2 is retired.
 *
 * Uses the authenticated /api/v1/browser route which passes through
 * Envoy's auth chain (JWT validation, User ID Mapper, Rakshak).
 *
 * @param podName - The browser pod name
 * @param orgId - Organization ID for the authenticated route
 * @param workspaceId - Workspace ID for the authenticated route
 * @returns The agent stream HTTP URL for SSE
 */
export function buildAgentStreamUrl(
  podName: string,
  orgId: string,
  workspaceId: string,
): string {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}/api/v1/browser/organizations/${orgId}/workspaces/${workspaceId}/agent/stream/${podName}`;
}

/**
 * Resolves a live_view_url to an agent stream SSE URL, handling both V2 and V4.
 *
 * - V4 session-native paths are normalized to:
 *   /api/v1/browser/namespaces/{ns}/[v2/]sessions/{id}/stream
 * - V2 uses pod-name based routing and requires URL rebuilding (V2 COMPAT).
 */
export function resolveAgentStreamUrl(
  liveViewUrl: string,
  orgId: string,
  workspaceId: string,
): string | null {
  if (liveViewUrl.startsWith('/api/v1/browser/namespaces/')) {
    const baseUrl = getApiBaseUrl();
    const match = liveViewUrl.match(
      /^(\/api\/v1\/browser\/namespaces\/[^/]+(?:\/v2)?\/sessions\/[^/]+)\/(?:screencast|stream)$/,
    );
    if (!match) return null;
    return `${baseUrl}${match[1]}/stream`;
  }
  const podName = extractPodNameFromUrl(liveViewUrl);
  if (!podName || !orgId || !workspaceId) return null;
  return buildAgentStreamUrl(podName, orgId, workspaceId);
}

/**
 * V2 COMPAT — Builds the screencast SSE URL for a V2 pod name.
 * V4 uses resolveScreencastUrl() directly. Remove when V2 is retired.
 *
 * Uses the authenticated /api/v1/browser route which passes through
 * Envoy's auth chain (JWT validation, User ID Mapper, Rakshak).
 *
 * The screencast endpoint streams JPEG frames via Server-Sent Events (SSE)
 * using CDP Page.startScreencast.
 *
 * @param podName - The browser pod name
 * @param orgId - Organization ID for the authenticated route
 * @param workspaceId - Workspace ID for the authenticated route
 * @returns The screencast SSE URL
 */
export function buildScreencastUrl(
  podName: string,
  orgId: string,
  workspaceId: string,
): string {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}/api/v1/browser/organizations/${orgId}/workspaces/${workspaceId}/screencast/${podName}`;
}

/**
 * Resolves a live_view_url to a screencast SSE URL, handling both V2 and V4.
 *
 * - V4 URLs are gateway-relative paths like
 *   /api/v1/browser/namespaces/{ns}/[v2/]sessions/{id}/screencast
 *   and are used directly.
 * - V2 URLs are cluster-internal like
 *   ws://service.svc.cluster.local/display/{pod} — need pod extraction
 *   and authenticated URL rebuilding (V2 COMPAT).
 */
export function resolveScreencastUrl(
  liveViewUrl: string,
  orgId: string,
  workspaceId: string,
): string | null {
  if (liveViewUrl.startsWith('/api/v1/browser/namespaces/')) {
    // Live view must use the dedicated screencast endpoint.
    if (!liveViewUrl.endsWith('/screencast')) return null;
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}${liveViewUrl}`;
  }
  const podName = extractPodNameFromUrl(liveViewUrl);
  if (!podName) return null;
  return buildScreencastUrl(podName, orgId, workspaceId);
}

/**
 * Resolves a live_view_url to the artifacts base URL for recordings.
 *
 * For V4 paths like /api/v1/browser/namespaces/{ns}/[v2/]sessions/{id}/screencast
 * (or /stream), returns
 * <gateway>/api/v1/browser/namespaces/{ns}/sessions/{id}/artifacts.
 *
 * Returns null for V2 (no recording support) or malformed inputs.
 */
export function resolveArtifactsBaseUrl(liveViewUrl: string): string | null {
  if (!liveViewUrl.startsWith('/api/v1/browser/namespaces/')) return null;
  const match = liveViewUrl.match(
    /^(\/api\/v1\/browser\/namespaces\/[^/]+(?:\/v2)?\/sessions\/[^/]+)\/(?:screencast|stream)$/,
  );
  if (!match) return null;
  return `${getApiBaseUrl()}${match[1]}/artifacts`;
}
